import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Toolbar, DataCard, StatusPill } from "@/components/admin/AdminPrimitives";
import { Skeleton } from "@/components/ui/skeleton";
import { Archive, ArchiveRestore, CheckCircle2, Copy, Pencil, Plus, Sparkles } from "lucide-react";
import {
  listBusinessJourneys,
  ensureBusinessTemplate,
  archiveTemplate,
  restoreTemplate,
  duplicateTemplate,
} from "@/lib/admin-journeys.functions";

export const Route = createFileRoute("/_authenticated/admin/conteudos/jornadas/")({
  head: () => ({
    meta: [
      { title: "Jornadas de implantação — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: JourneysListPage,
});

type ArchiveFilter = "active" | "archived" | "all";

function JourneysListPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "with" | "without" | "published" | "draft">("all");
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active");
  const nav = useNavigate();
  const qc = useQueryClient();
  const load = useServerFn(listBusinessJourneys);
  const ensure = useServerFn(ensureBusinessTemplate);
  const archive = useServerFn(archiveTemplate);
  const restore = useServerFn(restoreTemplate);
  const duplicate = useServerFn(duplicateTemplate);

  const list = useQuery({
    queryKey: ["admin.jornadas.list", archiveFilter],
    queryFn: () => load({ data: { filter: archiveFilter } }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin.jornadas.list"] });

  const ensureMut = useMutation({
    mutationFn: (businessId: string) => ensure({ data: { businessId } }),
    onSuccess: (res) => {
      invalidate();
      nav({
        to: "/admin/conteudos/jornadas/$templateId" as never,
        params: { templateId: (res as any).id },
      } as never);
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao criar jornada."),
  });

  const archiveMut = useMutation({
    mutationFn: (templateId: string) => archive({ data: { templateId } }),
    onSuccess: () => { toast.success("Jornada arquivada."); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao arquivar."),
  });

  const restoreMut = useMutation({
    mutationFn: (templateId: string) => restore({ data: { templateId } }),
    onSuccess: () => { toast.success("Jornada restaurada."); invalidate(); },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao restaurar."),
  });

  const dupMut = useMutation({
    mutationFn: (payload: { sourceTemplateId: string; newTitle?: string }) =>
      duplicate({ data: payload }),
    onSuccess: (res: any) => {
      toast.success("Cópia criada como rascunho.");
      invalidate();
      nav({
        to: "/admin/conteudos/jornadas/$templateId" as never,
        params: { templateId: res.templateId },
      } as never);
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao duplicar."),
  });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    const all = list.data?.businesses ?? [];
    return all.filter((b: any) => {
      if (s && ![b.title, b.slug, b.category].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)))
        return false;
      if (status === "with" && !b.template) return false;
      if (status === "without" && b.template) return false;
      if (status === "published" && !b.template?.published) return false;
      if (status === "draft" && (!b.template || b.template.published)) return false;
      // no rows for archived filter — show archived templates only in the row when it exists
      if (archiveFilter === "archived" && !b.template) return false;
      return true;
    });
  }, [q, status, archiveFilter, list.data]);

  const baseTemplates = list.data?.baseTemplates ?? [];

  return (
    <div>
      <PageHeader
        title="Jornadas de implantação"
        description="Configure o roteiro que o membro executa para implantar cada negócio."
      />

      <Toolbar search={q} onSearch={setQ} placeholder="Pesquisar por negócio, slug ou categoria…">
        <div className="flex items-center gap-1 rounded-lg border border-black/[0.08] bg-white p-0.5 text-xs">
          {(["active", "archived", "all"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setArchiveFilter(f)}
              className={`rounded-md px-2.5 py-1.5 font-medium transition ${
                archiveFilter === f ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)] hover:text-[var(--navy)]"
              }`}
            >
              {f === "active" ? "Ativos" : f === "archived" ? "Arquivados" : "Todos"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-black/[0.08] bg-white p-0.5 text-xs">
          {(["all", "with", "without", "published", "draft"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-md px-2.5 py-1.5 font-medium transition ${
                status === s ? "bg-[var(--navy)] text-white" : "text-[var(--gray-ink)] hover:text-[var(--navy)]"
              }`}
            >
              {s === "all" ? "Todos" :
               s === "with" ? "Com jornada" :
               s === "without" ? "Sem jornada" :
               s === "published" ? "Publicadas" : "Rascunhos"}
            </button>
          ))}
        </div>
      </Toolbar>

      {baseTemplates.length > 0 && (
        <DataCard>
          <div className="border-b border-black/[0.05] bg-[var(--gray-soft)]/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Jornadas-base (reutilizáveis)
          </div>
          <div className="divide-y divide-black/[0.05]">
            {baseTemplates.map((t: any) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[var(--navy)]">{t.title}</span>
                    <StatusPill tone="gray">Base</StatusPill>
                    {t.archived_at && <StatusPill tone="gray">Arquivada</StatusPill>}
                    {!t.archived_at && (t.published ? (
                      <StatusPill tone="green"><CheckCircle2 size={10} className="mr-1" /> Publicada</StatusPill>
                    ) : (
                      <StatusPill tone="gold">Rascunho</StatusPill>
                    ))}
                  </div>
                  <div className="text-xs text-[var(--gray-ink)]">{t.mission_count} missões</div>
                </div>
                <RowActions
                  templateId={t.id}
                  archived={!!t.archived_at}
                  onArchive={() => archiveMut.mutate(t.id)}
                  onRestore={() => restoreMut.mutate(t.id)}
                  onDuplicate={() => dupMut.mutate({ sourceTemplateId: t.id, newTitle: `${t.title} (cópia)` })}
                />
              </div>
            ))}
          </div>
        </DataCard>
      )}

      <DataCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--gray-soft)]/60 text-left text-[11px] uppercase tracking-wide text-[var(--gray-ink)]">
              <tr>
                <th className="px-4 py-3 font-semibold">Negócio</th>
                <th className="px-4 py-3 font-semibold">Categoria</th>
                <th className="px-4 py-3 font-semibold">Jornada</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Missões</th>
                <th className="px-4 py-3 text-right font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/[0.05]">
              {list.isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="p-2">
                      <Skeleton className="h-9 w-full" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-sm text-[var(--gray-ink)]">
                    Nenhum negócio encontrado.
                  </td>
                </tr>
              ) : (
                rows.map((b: any) => (
                  <tr key={b.id} className="hover:bg-[var(--gray-soft)]/30">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--navy)]">{b.title}</div>
                      <div className="text-xs text-[var(--gray-ink)]/70">{b.slug}</div>
                    </td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">{b.category ?? "—"}</td>
                    <td className="px-4 py-3 text-[var(--gray-ink)]">
                      {b.template ? b.template.title : <span className="italic opacity-60">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {b.template ? (
                        b.template.archived_at ? (
                          <StatusPill tone="gray">Arquivada</StatusPill>
                        ) : b.template.published ? (
                          <StatusPill tone="green">
                            <CheckCircle2 size={10} className="mr-1" /> Publicada
                          </StatusPill>
                        ) : (
                          <StatusPill tone="gold">Rascunho</StatusPill>
                        )
                      ) : (
                        <StatusPill tone="gray">Sem jornada</StatusPill>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--gray-ink)]">
                      {b.template?.mission_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {b.template ? (
                        <div className="inline-flex items-center gap-1">
                          <Link
                            to={"/admin/conteudos/jornadas/$templateId" as never}
                            params={{ templateId: b.template.id } as never}
                            className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
                          >
                            <Pencil size={12} /> Editar
                          </Link>
                          <RowActions
                            templateId={b.template.id}
                            archived={!!b.template.archived_at}
                            onArchive={() => archiveMut.mutate(b.template.id)}
                            onRestore={() => restoreMut.mutate(b.template.id)}
                            onDuplicate={() => dupMut.mutate({
                              sourceTemplateId: b.template.id,
                              newTitle: `${b.template.title} (cópia)`,
                            })}
                          />
                        </div>
                      ) : (
                        <button
                          onClick={() => ensureMut.mutate(b.id)}
                          disabled={ensureMut.isPending}
                          className="inline-flex items-center gap-1 rounded-md bg-[var(--navy)] px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                        >
                          <Plus size={12} /> Criar jornada
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DataCard>

      <div className="mt-6 flex items-start gap-3 rounded-xl border border-black/[0.06] bg-white p-4 text-xs text-[var(--gray-ink)]">
        <Sparkles size={14} className="mt-0.5 text-[var(--navy)]" />
        <div>
          <p className="font-semibold text-[var(--navy)]">Onda 2.B — arquivamento e duplicação</p>
          <p className="mt-1">
            Arquivar oculta a jornada de novas publicações preservando IDs e histórico. Restaurar
            traz de volta sem perda. Duplicar cria uma cópia limpa (novos IDs, dependências
            remapeadas) como rascunho, sem projetos, respostas ou métricas — jornada-base é
            reutilizável sem propagar para jornadas já personalizadas.
          </p>
        </div>
      </div>
    </div>
  );
}

function RowActions({
  templateId, archived, onArchive, onRestore, onDuplicate,
}: {
  templateId: string; archived: boolean;
  onArchive: () => void; onRestore: () => void; onDuplicate: () => void;
}) {
  void templateId;
  return (
    <>
      <button
        onClick={onDuplicate}
        title="Duplicar jornada"
        className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
      >
        <Copy size={12} />
      </button>
      {archived ? (
        <button
          onClick={onRestore}
          title="Restaurar jornada"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
        >
          <ArchiveRestore size={12} />
        </button>
      ) : (
        <button
          onClick={() => {
            if (confirm("Arquivar esta jornada? Ela some das listas ativas e não pode ser publicada, mas nada é apagado.")) onArchive();
          }}
          title="Arquivar jornada"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 bg-white px-2 py-1.5 text-xs font-semibold text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
        >
          <Archive size={12} />
        </button>
      )}
    </>
  );
}
